import { api } from "./client";
import type { PagedQuery, PagedResult } from "@/types/api";

// ---------- Enums (mirrors PhoenixCapital.Core.Enums.Commissions) ----------

export type ClaimStatus =
  | "Draft"
  | "Submitted"
  | "UnderReview"
  | "Contested"
  | "Revised"
  | "Withdrawn"
  | "Agreed"
  | "Payable"
  | "PartiallyPaid"
  | "PaidInFull"
  | "Suggested";

export type RevisionState = "None" | "RevisionInProgress";

export type PaymentMethod = "Check" | "ACH" | "Wire" | "Payroll" | "Other";
export type AdjustmentType = "Correction" | "Clawback" | "Supplemental";
export type CommissionAuditAction =
  | "Created"
  | "Submitted"
  | "MovedToUnderReview"
  | "Contested"
  | "Revised"
  | "Agreed"
  | "RevenueConfirmed"
  | "PaymentRecorded"
  | "PaymentAdjusted"
  | "Escalated"
  | "EscalationResolved"
  | "Withdrawn"
  | "FieldEdited"
  | "CommentAdded"
  | "PeriodLocked"
  | "PeriodUnlocked"
  | "RevisionStarted"
  | "RevisionDiscarded"
  | "Suggested"
  | "Accepted"
  // The claimant answered a contest WITHOUT changing anything. The SPA renders
  // an audit entry's action RAW, so this string is also the on-screen label.
  | "Affirmed";

// ---------- Pools ----------

export interface PoolSuggestedDescriptionDto {
  id: string;
  revenuePoolId: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
}

export interface RevenuePoolDto {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  exclusions?: string | null;
  sortOrder: number;
  isRetired: boolean;
  claimCount: number;
  suggestedDescriptions: PoolSuggestedDescriptionDto[];
  /** False for reporting-only pools that never fund a commission (Pool G —
   * "Phoenix Interest & Service Income"). */
  isCommissionable: boolean;
}

export interface UpsertRevenuePoolRequest {
  code: string;
  name: string;
  description?: string | null;
  exclusions?: string | null;
  sortOrder: number;
}

export interface UpsertPoolDescriptionRequest {
  description: string;
  sortOrder: number;
  isActive: boolean;
}

// ---------- Claims ----------

export interface CommissionClaimSummaryDto {
  id: string;
  claimNumber: string;
  claimantUserId: string;
  claimantDisplayName?: string | null;
  loanId?: string | null;
  loanReference?: string | null;
  /**
   * The loan number behind `loanId`, resolved server-side for display.
   * Null when the claim has no `loanId` (a recurring-pool roll-up spans many
   * loans) or the loan did not resolve. Render via <ClaimLoan/>, which owns the
   * fallback ladder, rather than branching on it here.
   *
   * Read-only: it is NOT on CreateClaimRequest / UpdateClaimRequest, because
   * the client never supplies a loan number — it supplies `loanId`.
   */
  loanNumber?: string | null;
  /**
   * Why this claim is HELD — one of `HeldReasonCode`, or absent.
   *
   * Present only for a held claim (Suggested with an empty claimant) on a BOOK
   * position that names a loan. Absent covers four different states and a
   * renderer must not collapse them: not held; a monthly roll-up, whose held
   * SUMMARY rides `serviceDescription` because the row persists no loan set to
   * break down; a non-book position, where `heldPosition` explains the hold as
   * a role COUNT instead; or a hold the next routing pass will clear.
   *
   * Read it through `HELD_REASON_COPY`, never rendered raw: the codes are
   * server vocabulary and the surface owns its words.
   */
  heldReason?: HeldReasonCode | null;
  /** The specifics behind `heldReason` when the code alone cannot be acted on
   *  — the date behind "no team history", the competing lead ids behind
   *  "multiple buyer leads". Free text; render it beside the code, never
   *  instead of it. */
  heldReasonDetail?: string | null;
  revenuePoolId: string;
  poolCode: string;
  poolName: string;
  serviceDescription: string;
  grossRevenue: number;
  commissionPercent: number;
  commissionAmount: number;
  totalPaid: number;
  balanceRemaining: number;
  status: ClaimStatus;
  revisionState: RevisionState;
  reviewRound: number;
  isEscalated: boolean;
  periodYear: number;
  periodMonth: number;
  createdAt: string;
  /** When the claim entered the review queue: set on submit and on accepting a
   *  suggestion. Null for a claim that has not been submitted, or one
   *  submitted before the server recorded it. The review queue orders by
   *  `submittedAt ?? createdAt`, so a surface showing when a claim was
   *  submitted falls back the same way. */
  submittedAt?: string | null;
  modifiedAt?: string | null;
  roleCode?: string | null;
  paymentId?: string | null;
  dealId?: string | null;
}

export interface CommissionAuditLogDto {
  id: string;
  commissionClaimId: string;
  commissionPaymentId?: string | null;
  action: CommissionAuditAction;
  userId: string;
  userDisplayName?: string | null;
  timestamp: string;
  notes?: string | null;
  contestedFields?: string | null;
  suggestedValues?: string | null;
  oldValues?: string | null;
  newValues?: string | null;
  /**
   * Admin User Impersonation — the admin who was driving when this row was
   * written, or absent for the overwhelming majority of rows (a genuine
   * session). `userId`/`userDisplayName` above stay the acting identity, which
   * during an impersonated session is the TARGET.
   *
   * Absent on any row written before the column existed: that means UNKNOWN,
   * not "not impersonated".
   */
  impersonatedByUserId?: string | null;
  impersonatedByDisplayName?: string | null;
}

export interface ClaimCommentDto {
  id: string;
  commissionClaimId: string;
  parentCommentId?: string | null;
  userId: string;
  userDisplayName?: string | null;
  content: string;
  isReadByCounterparty: boolean;
  readAt?: string | null;
  createdAt: string;
}

export interface CommissionPaymentDto {
  id: string;
  paymentNumber: string;
  commissionClaimId: string;
  claimNumber: string;
  paymentDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  batchId?: string | null;
  adjustmentType?: AdjustmentType | null;
  adjustmentReason?: string | null;
  notes?: string | null;
  recordedByUserId: string;
  createdAt: string;
}

export interface CommissionFundingEventDto {
  id: string;
  incomeEventId: string;
  amount: number;
  recognizedAt: string;
  // The same two facts the candidate picker shows. Optional so existing mocks
  // and older payloads stay valid (the `fundingEvents` precedent below). Without
  // them two funding events of the same amount on the same day — which is what a
  // recurring pool's monthly roll-up routinely produces — are indistinguishable.
  sourceType?: string;
  sourceLabel?: string | null;
}

export interface CommissionFundingCandidateDto {
  incomeEventId: string;
  amount: number;
  recognizedAt: string;
  sourceType: string;
  sourceLabel?: string | null;
  revenuePoolId: string;
  poolCode: string;
  alreadyLinked: boolean;
}

export interface CommissionClaimDetailDto {
  summary: CommissionClaimSummaryDto;
  revenueReceivedDate?: string | null;
  escalationResolution?: string | null;
  escalatedByUserId?: string | null;
  resolvedByUserId?: string | null;
  linkedClaimId?: string | null;
  templateId?: string | null;
  payments: CommissionPaymentDto[];
  auditEntries: CommissionAuditLogDto[];
  comments: ClaimCommentDto[];
  // SP-5 WS-3 — event-backed revenue confirmation (optional so existing mocks
  // and older payloads stay valid).
  revenueConfirmedByUserId?: string | null;
  revenueConfirmedByDisplayName?: string | null;
  revenueConfirmedAt?: string | null;
  legacyRevenueConfirmation?: boolean;
  fundingEvents?: CommissionFundingEventDto[];
  // The claimant's own withdrawal reason, the reversal sweep's, or the
  // recurring roll-up's supersession message — three writers that had no
  // reader until now. Optional so existing mocks and older payloads stay valid.
  disqualificationReason?: string | null;
  // Present only while the claim is HELD (Suggested with an empty claimant).
  heldPosition?: HeldPositionDto | null;
}

/**
 * Mirrors `PhoenixCapital.Core.Enums.Commissions.HeldPositionRouting`.
 *
 * 🔴 KEEP THIS EXHAUSTIVE AND KEEP IT READ BY A `Record<>`. The server's
 * classifier is a ternary chain ending in an unconditional `FirmWide`, so a new
 * tier cannot fail to compile there — it is pinned by a test instead. On this
 * side it CAN fail to compile, and `ClaimHeld.tsx` keys its sentences off a
 * `Record<HeldPositionRouting, …>` precisely so a member added here without
 * copy is a typecheck error rather than an `else` arm asserting something false
 * about the new tier. That is not hypothetical: `Book` arrived into an `else`
 * that said "held on the deal rather than the loan", which is wrong twice over.
 */
export type HeldPositionRouting =
  | "LoanPill"
  | "DealAssignment"
  | "FirmWide"
  | "Book";

/**
 * Mirrors the VALUES of `PhoenixCapital.Core.Constants.CommissionAttributionHeldReasons`.
 *
 * 🔴 KEEP IT EXHAUSTIVE AND KEEP IT READ BY A `Record<>`. `ClaimHeld.tsx`
 * keys its sentences off a `Record<HeldReasonCode, …>`, so a member added here
 * without copy is a typecheck error rather than a blank cell beside a held
 * claim — which is the state the whole held-claim treatment exists to remove.
 * The server cannot help: its side is a list of `const string`, which no
 * compiler checks against this union. `heldReasonCodes.parity.test.ts` reads
 * the C# and pins this against it, so a member added THERE and not here fails a
 * test.
 *
 * The two groups are not interchangeable and the copy says which is which: a
 * STAMPED reason is a fact about the closing and is cleared only by an ADMIN
 * correction to the loan's attribution; a RE-CHECKED reason is a fact about
 * today and clears itself when the condition does.
 */
export type HeldReasonCode =
  // Stamped at closing.
  | "attribution not stamped"
  | "multiple buyer leads"
  | "no specialist at closing"
  | "specialist on no team"
  | "team has no single f1 manager"
  | "no broker liaison"
  | "no team history"
  // Re-checked on every routing pass.
  | "payee deactivated"
  | "month already accepted";

/**
 * Why a claim is held, and therefore what would unhold it.
 *
 * 🔴 `firmWideHolderCount` is the whole point of this object. The server
 * resolves a firm-wide role with `ids.Count == 1 ? ids[0] : null`, so ZERO
 * holders and TWO-OR-MORE holders both arrive as "unresolved" and are otherwise
 * indistinguishable — while needing OPPOSITE actions from the reader (grant the
 * role, versus take it off someone). Without the count a UI can only say
 * "held", which is the thing that tells nobody anything.
 */
export interface HeldPositionDto {
  roleCode: string;
  routing: HeldPositionRouting;
  /** Rows in UserRoleCodes for this role, with NO IsActive filter — the
   *  resolver applies none either, and a count that disagreed with the
   *  resolution it explains would be worse than no count.
   *
   *  🔴 ZERO on every tier but `FirmWide`, and that is deliberate rather
   *  than unfilled. Only the firm-wide tier resolves on a count. On `Book` a
   *  count would actively mislead: several people holding F1 is the DESIGNED
   *  state, so "2 hold F1" beside a held share invites the one remedy that
   *  cannot work. Never render it outside the firm-wide arm. */
  firmWideHolderCount: number;
}

export interface CreateClaimRequest {
  loanId?: string | null;
  loanReference?: string | null;
  revenuePoolId: string;
  serviceDescription: string;
  grossRevenue: number;
  commissionPercent: number;
  templateId?: string | null;
  linkedClaimId?: string | null;
}

/**
 * Edit or revise a claim.
 *
 * The two loan fields follow the FIND-12 clear contract, because both used to
 * be applied unconditionally server-side: a payload that simply omitted
 * `loanId` unlinked the claim from its loan, silently.
 *
 * - `loanId` — omit or send null to LEAVE IT ALONE; send a value to set it;
 *   set `clearLoanId` to remove it. A nullable id has no empty value of its
 *   own, so the flag is the only way to express "remove".
 * - `loanReference` — omit or send null to leave it alone; send `""` to clear
 *   it; send text to set it.
 */
export interface UpdateClaimRequest {
  loanId?: string | null;
  loanReference?: string | null;
  revenuePoolId: string;
  serviceDescription: string;
  grossRevenue: number;
  commissionPercent: number;
  revisionSummary?: string | null;
  clearLoanId?: boolean;
}

export interface ClaimListQuery extends PagedQuery {
  status?: ClaimStatus;
  poolId?: string;
  claimantId?: string;
  dateFrom?: string;
  dateTo?: string;
  periodYear?: number;
  periodMonth?: number;
  isEscalated?: boolean;
  minAmount?: number;
  maxAmount?: number;
  onlyMine?: boolean;
}

export interface ContestClaimRequest {
  notes: string;
  contestedFields: string[];
  suggestedValues?: Record<string, unknown> | null;
}

export interface ConfirmRevenueRequest {
  // SP-5 WS-3 — confirmation links the funding income event(s); the received
  // date is derived server-side (no typed date).
  incomeEventIds: string[];
  notes?: string | null;
}

/** The claimant's answer to a contest when the original figures were right.
 *  Carries an explanation and NOTHING ELSE — the server has no value fields to
 *  assign, so "an affirm cannot edit values" holds by construction. */
export interface AffirmClaimRequest {
  explanation: string;
}

export interface EscalateRequest {
  reason: string;
}

export interface ResolveEscalationRequest {
  resolution: string;
  setCommissionAmount?: number | null;
  setGrossRevenue?: number | null;
  setCommissionPercent?: number | null;
}

export interface DuplicateCheckRequest {
  claimantUserId?: string | null;
  loanId?: string | null;
  poolId: string;
  windowDays?: number;
}

export interface DuplicateCheckResult {
  matches: CommissionClaimSummaryDto[];
}

// ---------- Payments ----------

export interface RecordPaymentRequest {
  paymentDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  notes?: string | null;
}

export interface BatchPaymentRequest {
  claimIds: string[];
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  notes?: string | null;
}

export interface AdjustmentRequest {
  adjustmentType: AdjustmentType;
  amount: number;
  adjustmentReason: string;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
}

// ---------- Templates / Periods / Comments ----------

export interface ClaimTemplateDto {
  id: string;
  userId: string;
  name: string;
  revenuePoolId: string;
  poolCode: string;
  poolName: string;
  defaultDescription?: string | null;
  defaultCommissionPercent?: number | null;
  createdAt: string;
}

export interface UpsertClaimTemplateRequest {
  name: string;
  revenuePoolId: string;
  defaultDescription?: string | null;
  defaultCommissionPercent?: number | null;
}

export interface CommissionPeriodDto {
  id: string;
  year: number;
  month: number;
  isLocked: boolean;
  lockedByUserId?: string | null;
  lockedAt?: string | null;
  unlockedByUserId?: string | null;
  unlockedAt?: string | null;
  claimCount: number;
  paidTotal: number;
}

export interface CreateCommentRequest {
  content: string;
  parentCommentId?: string | null;
}

// ---------- Stats / reports ----------

export interface MyClaimsStatsDto {
  ytdEarned: number;
  pendingPayment: number;
  awaitingRevenue: number;
  inNegotiation: number;
  contestedCount: number;
  draftCount: number;
  draftTotal: number;
}

export interface AccountingQueueStatsDto {
  reviewDepth: number;
  revenueConfirmDepth: number;
  paymentDepth: number;
  escalationDepth: number;
  /** Contested claims waiting on their claimants, escalated ones excluded. */
  awaitingClaimantDepth: number;
}

export interface MyEarningsMonthDto {
  month: number;
  earned: number;
}
export interface MyEarningsPoolDto {
  poolId: string;
  poolCode: string;
  poolName: string;
  earned: number;
}
export interface MyEarningsReportDto {
  year: number;
  totalEarnedYtd: number;
  totalPendingPayment: number;
  totalAwaitingRevenue: number;
  totalInNegotiation: number;
  byMonth: MyEarningsMonthDto[];
  byPool: MyEarningsPoolDto[];
}

export interface AgingBucketDto {
  label: string;
  minDays: number;
  maxDays?: number | null;
  claimCount: number;
  totalAmount: number;
}
export interface AgingReportDto {
  buckets: AgingBucketDto[];
}

export interface PendingLiabilityDto {
  totalAgreed: number;
  totalPayable: number;
  totalPartiallyPaid: number;
  totalPending: number;
}

export interface PoolSummaryDto {
  poolId: string;
  poolCode: string;
  poolName: string;
  totalRevenue: number;
  totalCommissions: number;
  loadRatio: number;
  // SP-5 WS-4 — Pool F teaching state + visible unallocated accrual.
  recognizedIncome: number;
  hasAllocations: boolean;
  unallocatedAccrual: number;
}

export interface TeamMemberBreakdownDto {
  userId: string;
  displayName?: string | null;
  totalClaims: number;
  totalEarned: number;
  totalOutstanding: number;
  agreementRatePercent: number;
}
export interface ManagerOverviewDto {
  byPool: MyEarningsPoolDto[];
  byTeamMember: TeamMemberBreakdownDto[];
  totalOutstanding: number;
  totalPaidYtd: number;
}

// ---------- Admin browse views (PR-6) ----------

export interface AdminClaimListQuery extends PagedQuery {
  /** Comma-separated ClaimStatus values, e.g. "Submitted,UnderReview". */
  statuses?: string;
  /** Comma-separated pool Guids. */
  poolIds?: string;
  claimantId?: string;
  loanRef?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  revisionState?: RevisionState;
}

export interface AdminPaymentListQuery extends PagedQuery {
  poolIds?: string;
  claimantId?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
}

export interface AdminPaymentRowDto {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  commissionClaimId: string;
  claimNumber: string;
  claimantUserId: string;
  claimantDisplayName?: string | null;
  revenuePoolId: string;
  poolCode: string;
  poolName: string;
  amount: number;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  batchId?: string | null;
  adjustmentType?: AdjustmentType | null;
  adjustmentReason?: string | null;
  notes?: string | null;
  recordedByUserId: string;
  recordedByDisplayName?: string | null;
  createdAt: string;
}

export interface PoolPaymentAggregateDto {
  poolId: string;
  poolCode: string;
  poolName: string;
  total: number;
}

export interface PaymentAggregatesDto {
  totalPaidYtd: number;
  totalPaidMtd: number;
  totalPaidFiltered: number;
  filteredCount: number;
  perPool: PoolPaymentAggregateDto[];
}

// ---------- Rate admin: allocation table + per-user overrides ----------

export interface CommissionAllocationDto {
  id: string;
  roleCode: string;
  roleTitle: string;
  revenuePoolId: string;
  poolCode: string;
  poolName: string;
  poolIsRetired: boolean;
  percent: number;
}

export interface UpsertCommissionAllocationRequest {
  roleCode: string;
  revenuePoolId: string;
  percent: number;
}

/** A selectable position for the allocation editor — RoleCodes.All plus the
 *  commission-only E3-E5. A picker source, not a validation contract: the
 *  create endpoint deliberately does not check roleCode against it. */
export interface CommissionPositionDto {
  code: string;
  title: string;
}

/** What deleting an allocation touches. Every count is informational —
 *  existing claims froze their own percent/amount at draft time, so the real
 *  consequence is prospective (auto-suggest stops; the rate grid falls back
 *  to 0.00% for holders with no override). */
export interface CommissionAllocationDeleteImpactDto {
  roleCode: string;
  roleTitle: string;
  poolCode: string;
  poolName: string;
  poolIsRetired: boolean;
  percent: number;
  claimCount: number;
  roleHolderCount: number;
  orphanedOverrideCount: number;
  poolHasRecognizedIncome: boolean;
  /** Allocations this position holds on OTHER pools. Zero means deleting this
   *  row un-commissions the position, so its holders leave the rates grid
   *  entirely rather than showing 0.00% — a materially different consequence
   *  the copy has to branch on. */
  otherAllocationCountForPosition: number;
}

export interface RateGridPoolDto {
  poolId: string;
  code: string;
  name: string;
}

export interface RateGridCellDto {
  poolId: string;
  default: number | null;
  override: number | null;
  effective: number;
}

export interface RateGridRowDto {
  userId: string;
  displayName: string | null;
  roleCode: string;
  roleTitle: string;
  cells: RateGridCellDto[];
}

export interface CommissionRateGridDto {
  pools: RateGridPoolDto[];
  rows: RateGridRowDto[];
}

export interface SetRateOverrideRequest {
  userId: string;
  revenuePoolId: string;
  percent: number;
}

export interface ClearRateOverrideRequest {
  userId: string;
  revenuePoolId: string;
}

// ---------- API facade ----------

export const commissionsApi = {
  // Claims
  listClaims: (query?: ClaimListQuery) =>
    api.get<PagedResult<CommissionClaimSummaryDto>>(
      "/commissions/claims",
      query,
    ),
  getClaim: (id: string) =>
    api.get<CommissionClaimDetailDto>(`/commissions/claims/${id}`),
  createClaim: (body: CreateClaimRequest) =>
    api.post<CommissionClaimDetailDto>("/commissions/claims", body),
  updateClaim: (id: string, body: UpdateClaimRequest) =>
    api.put<CommissionClaimDetailDto>(`/commissions/claims/${id}`, body),
  checkDuplicate: (body: DuplicateCheckRequest) =>
    api.post<DuplicateCheckResult>("/commissions/claims/check-duplicate", body),

  submit: (id: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/submit`),
  accept: (id: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/accept`),
  review: (id: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/review`),
  agree: (id: string, notes?: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/agree`, {
      notes,
    }),
  contest: (id: string, body: ContestClaimRequest) =>
    api.post<CommissionClaimDetailDto>(
      `/commissions/claims/${id}/contest`,
      body,
    ),
  revise: (id: string, body: UpdateClaimRequest) =>
    api.post<CommissionClaimDetailDto>(
      `/commissions/claims/${id}/revise`,
      body,
    ),
  affirm: (id: string, body: AffirmClaimRequest) =>
    api.post<CommissionClaimDetailDto>(
      `/commissions/claims/${id}/affirm`,
      body,
    ),
  withdraw: (id: string, reason?: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/withdraw`, {
      reason,
    }),
  fundingCandidates: (id: string) =>
    api.get<CommissionFundingCandidateDto[]>(
      `/commissions/claims/${id}/funding-candidates`,
    ),
  confirmRevenue: (id: string, body: ConfirmRevenueRequest) =>
    api.post<CommissionClaimDetailDto>(
      `/commissions/claims/${id}/confirm-revenue`,
      body,
    ),
  escalate: (id: string, reason: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/escalate`, {
      reason,
    }),
  resolveEscalation: (id: string, body: ResolveEscalationRequest) =>
    api.post<CommissionClaimDetailDto>(
      `/commissions/claims/${id}/resolve-escalation`,
      body,
    ),

  clone: (id: string) =>
    api.post<CommissionClaimDetailDto>(`/commissions/claims/${id}/clone`),
  bulkSubmit: (ids: string[]) =>
    api.post<CommissionClaimDetailDto[]>("/commissions/claims/bulk-submit", {
      ids,
    }),
  // SP-5 WS-3 — bulk confirm auto-links each suggestion-born claim's source
  // income; manual claims are skipped (diff request vs result).
  bulkConfirm: (ids: string[]) =>
    api.post<CommissionClaimDetailDto[]>("/commissions/claims/bulk-confirm", {
      ids,
    }),

  // Queues + stats
  reviewQueue: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/review"),
  revenueConfirmQueue: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/revenue-confirm"),
  paymentQueue: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/payment"),
  escalationQueue: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/escalation"),
  awaitingClaimantQueue: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/awaiting-claimant"),
  myAttention: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/my-attention"),
  mySuggested: () =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/queue/my-suggested"),
  queuePosition: (id: string) =>
    api.get<number | null>(`/commissions/claims/${id}/queue-position`),
  myStats: (year?: number) =>
    api.get<MyClaimsStatsDto>(
      "/commissions/stats/mine",
      year ? { year } : undefined,
    ),
  queueStats: () =>
    api.get<AccountingQueueStatsDto>("/commissions/stats/queue"),
  similar: (poolId: string, loanId?: string | null, take = 5) =>
    api.get<CommissionClaimSummaryDto[]>("/commissions/claims/similar", {
      poolId,
      loanId: loanId ?? undefined,
      take,
    }),

  // Pools
  listPools: (includeRetired = true) =>
    api.get<RevenuePoolDto[]>("/commissions/pools", { includeRetired }),
  getPool: (id: string) => api.get<RevenuePoolDto>(`/commissions/pools/${id}`),
  createPool: (body: UpsertRevenuePoolRequest) =>
    api.post<RevenuePoolDto>("/commissions/pools", body),
  updatePool: (id: string, body: UpsertRevenuePoolRequest) =>
    api.put<RevenuePoolDto>(`/commissions/pools/${id}`, body),
  retirePool: (id: string) =>
    api.patch<RevenuePoolDto>(`/commissions/pools/${id}/retire`),
  addDescription: (poolId: string, body: UpsertPoolDescriptionRequest) =>
    api.post<PoolSuggestedDescriptionDto>(
      `/commissions/pools/${poolId}/descriptions`,
      body,
    ),
  updateDescription: (
    poolId: string,
    descriptionId: string,
    body: UpsertPoolDescriptionRequest,
  ) =>
    api.put<PoolSuggestedDescriptionDto>(
      `/commissions/pools/${poolId}/descriptions/${descriptionId}`,
      body,
    ),
  removeDescription: (poolId: string, descriptionId: string) =>
    api.delete<void>(
      `/commissions/pools/${poolId}/descriptions/${descriptionId}`,
    ),

  // Payments
  listPayments: (query?: PagedQuery) =>
    api.get<PagedResult<CommissionPaymentDto>>("/commissions/payments", query),
  listMyPayments: (query?: PagedQuery) =>
    api.get<PagedResult<CommissionPaymentDto>>(
      "/commissions/payments/my",
      query,
    ),
  recordPayment: (claimId: string, body: RecordPaymentRequest) =>
    api.post<CommissionPaymentDto>(
      `/commissions/claims/${claimId}/payments`,
      body,
    ),
  batchPayment: (body: BatchPaymentRequest) =>
    api.post<CommissionPaymentDto[]>("/commissions/payments/batch", body),
  adjustment: (claimId: string, body: AdjustmentRequest) =>
    api.post<CommissionPaymentDto>(
      `/commissions/claims/${claimId}/adjustments`,
      body,
    ),

  // Templates
  listTemplates: () => api.get<ClaimTemplateDto[]>("/commissions/templates"),
  createTemplate: (body: UpsertClaimTemplateRequest) =>
    api.post<ClaimTemplateDto>("/commissions/templates", body),
  updateTemplate: (id: string, body: UpsertClaimTemplateRequest) =>
    api.put<ClaimTemplateDto>(`/commissions/templates/${id}`, body),
  deleteTemplate: (id: string) =>
    api.delete<void>(`/commissions/templates/${id}`),

  // Periods
  listPeriods: () => api.get<CommissionPeriodDto[]>("/commissions/periods"),
  lockPeriod: (year: number, month: number) =>
    api.post<CommissionPeriodDto>(`/commissions/periods/${year}/${month}/lock`),
  unlockPeriod: (year: number, month: number) =>
    api.post<CommissionPeriodDto>(
      `/commissions/periods/${year}/${month}/unlock`,
    ),

  // Comments
  listComments: (claimId: string) =>
    api.get<ClaimCommentDto[]>(`/commissions/claims/${claimId}/comments`),
  addComment: (claimId: string, body: CreateCommentRequest) =>
    api.post<ClaimCommentDto>(`/commissions/claims/${claimId}/comments`, body),
  markCommentRead: (claimId: string, commentId: string) =>
    api.patch<ClaimCommentDto>(
      `/commissions/claims/${claimId}/comments/${commentId}/read`,
    ),

  // Reports
  myEarnings: (year?: number) =>
    api.get<MyEarningsReportDto>(
      "/commissions/reports/my-earnings",
      year ? { year } : undefined,
    ),
  myOutstanding: () =>
    api.get<PagedResult<CommissionClaimSummaryDto>>(
      "/commissions/reports/my-outstanding",
    ),
  aging: () => api.get<AgingReportDto>("/commissions/reports/aging"),
  pendingLiability: () =>
    api.get<PendingLiabilityDto>("/commissions/reports/pending-liability"),
  poolSummary: (year?: number) =>
    api.get<PoolSummaryDto[]>(
      "/commissions/reports/pool-summary",
      year ? { year } : undefined,
    ),
  managerOverview: (year?: number) =>
    api.get<ManagerOverviewDto>(
      "/commissions/reports/manager-overview",
      year ? { year } : undefined,
    ),

  // Admin browse views (PR-6)
  adminListClaims: (query: AdminClaimListQuery) =>
    api.get<PagedResult<CommissionClaimSummaryDto>>(
      "/admin/commissions/claims",
      query,
    ),
  adminListPayments: (query: AdminPaymentListQuery) =>
    api.get<PagedResult<AdminPaymentRowDto>>(
      "/admin/commissions/payments",
      query,
    ),
  adminPaymentAggregates: (query: AdminPaymentListQuery) =>
    api.get<PaymentAggregatesDto>(
      "/admin/commissions/payments/aggregates",
      query,
    ),
  adminClaimsExportUrl: (query: AdminClaimListQuery) =>
    buildAdminExportUrl("/admin/commissions/claims/export", query),
  adminPaymentsExportUrl: (query: AdminPaymentListQuery) =>
    buildAdminExportUrl("/admin/commissions/payments/export", query),

  // Rate admin — allocation table (Commission.ManageAllocations)
  listAllocations: () =>
    api.get<CommissionAllocationDto[]>("/commissions/rates/allocations"),
  createAllocation: (body: UpsertCommissionAllocationRequest) =>
    api.post<CommissionAllocationDto>("/commissions/rates/allocations", body),
  updateAllocation: (id: string, body: UpsertCommissionAllocationRequest) =>
    api.put<CommissionAllocationDto>(
      `/commissions/rates/allocations/${id}`,
      body,
    ),
  deleteAllocation: (id: string) =>
    api.delete<void>(`/commissions/rates/allocations/${id}`),
  listAllocationPositions: () =>
    api.get<CommissionPositionDto[]>(
      "/commissions/rates/allocations/positions",
    ),
  allocationDeleteImpact: (id: string) =>
    api.get<CommissionAllocationDeleteImpactDto>(
      `/commissions/rates/allocations/${id}/delete-impact`,
    ),

  // Rate admin — per-user overrides grid (Commission.ManageRates)
  rateGrid: () => api.get<CommissionRateGridDto>("/commissions/rates/grid"),
  setRateOverride: (body: SetRateOverrideRequest) =>
    api.put<void>("/commissions/rates/overrides", body),
  clearRateOverride: (body: ClearRateOverrideRequest) =>
    api.delete<void>("/commissions/rates/overrides", body),
};

// CSV exports stream `text/csv` (not the ApiResponse JSON envelope), so
// they cannot route through `api.get`. The page navigates the browser to
// this URL inside an <a download> click — credentials: 'include' is
// implicit on a same-origin request, and the server emits a
// Content-Disposition header.
//
// Parameter is typed as `object` (not `Record<string, unknown>`) to
// match the codebase's QueryParams convention in api/client.ts. That
// lets typed query interfaces (which lack an index signature) flow
// through without an `as` cast.
function buildAdminExportUrl(path: string, query: object): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? "/api";
  const url = `${apiBase}${path}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}
